/**
 * Daily Insight Card v3.3
 *
 * Enhancements:
 * 1. Emotional-tone accent color shifts the card's top bar + quote hero
 * 2. Child quote typewriter reveal
 * 3. Overall score counter animates from 0 → value
 * 4. Staggered dimension-row entry (50 ms apart)
 * 5. Per-dimension delta vs previous session (▲/▼)
 * 6. 5-session sparkline next to overall score
 * 7. WhatsApp share button
 * 8. Milestone badges (session & streak achievements)
 */
import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import html2canvas from 'html2canvas';
import {
  X, Download, Loader2, Star, Target, MessageCircle, Heart, Sparkles,
  TrendingUp, BarChart3, Trophy, Shield, Leaf, Quote, Compass,
  ArrowUp, ArrowDown, Minus, RefreshCw, CheckCircle2, AlertCircle,
  Zap, Calendar, Flame, Award, Share2, ChevronDown, ChevronUp, FileText,
} from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* ─── dimension metadata ─────────────────────────────────────────────────── */
const DIM_META = {
  emotional_awareness:    { label: 'Emotional Awareness',     emoji: '😊', icon: Heart,         accent: 'text-rose-500',    ring: 'bg-rose-100',    bar: '#f43f5e' },
  intellectual_curiosity: { label: 'Intellectual Curiosity',  emoji: '🧠', icon: Sparkles,      accent: 'text-teal-600',    ring: 'bg-teal-100',    bar: '#0d9488' },
  social_connections:     { label: 'Social Connections',      emoji: '🤝', icon: MessageCircle, accent: 'text-violet-600',  ring: 'bg-violet-100',  bar: '#7c3aed' },
  creativity:             { label: 'Creativity',              emoji: '🎨', icon: Sparkles,      accent: 'text-amber-600',   ring: 'bg-amber-100',   bar: '#d97706' },
  physical_activity:      { label: 'Physical Activity',       emoji: '🏃', icon: TrendingUp,    accent: 'text-orange-600',  ring: 'bg-orange-100',  bar: '#ea580c' },
  values_in_action:       { label: 'Values in Action',        emoji: '🧭', icon: Shield,        accent: 'text-emerald-600', ring: 'bg-emerald-100', bar: '#16a34a' },
  planning_and_thinking:  { label: 'Planning & Independence', emoji: '🧩', icon: Compass,       accent: 'text-indigo-600',  ring: 'bg-indigo-100',  bar: '#4f46e5' },
};

const SPOTLIGHT_LABELS = {
  emotional_awareness:    'Emotional Awareness',
  intellectual_curiosity: 'Intellectual Curiosity',
  social_connections:     'Social Connections',
  creativity:             'Creativity',
  physical_activity:      'Physical Activity',
  values_in_action:       'Values in Action',
  planning_and_thinking:  'Planning & Independence',
};

/* ─── 1. Emotional-tone accent palette ──────────────────────────────────── */
const TONE_ACCENTS = {
  Joyful:     { bar: 'linear-gradient(90deg,#F0A820,#f59e0b,#fbbf24)', quoteFrom: '#FBE7C6', quoteTo: '#fde68a' },
  Settled:    { bar: 'linear-gradient(90deg,#0d9488,#14b8a6,#34d399)', quoteFrom: '#d1fae5', quoteTo: '#a7f3d0' },
  Reflective: { bar: 'linear-gradient(90deg,#6366f1,#8b5cf6,#a78bfa)', quoteFrom: '#ede9fe', quoteTo: '#ddd6fe' },
  Thoughtful: { bar: 'linear-gradient(90deg,#254B70,#3b82f6,#60a5fa)', quoteFrom: '#dbeafe', quoteTo: '#bfdbfe' },
  Restless:   { bar: 'linear-gradient(90deg,#ea580c,#f97316,#fb923c)', quoteFrom: '#ffedd5', quoteTo: '#fed7aa' },
  Heavy:      { bar: 'linear-gradient(90deg,#7c3aed,#a855f7,#c084fc)', quoteFrom: '#f3e8ff', quoteTo: '#e9d5ff' },
  Anxious:    { bar: 'linear-gradient(90deg,#db2777,#ec4899,#f472b6)', quoteFrom: '#fce7f3', quoteTo: '#fbcfe8' },
  Excited:    { bar: 'linear-gradient(90deg,#16a34a,#22c55e,#4ade80)', quoteFrom: '#dcfce7', quoteTo: '#bbf7d0' },
};
const DEFAULT_ACCENT = { bar: 'linear-gradient(90deg,#254B70,#F0A820,#258010)', quoteFrom: '#FBE7C6', quoteTo: '#F5D18C' };

/* ─── 8. Milestone badges ────────────────────────────────────────────────── */
const getMilestones = (sessions, streak) => {
  const m = [];
  const SESSION_MILESTONES = { 1: ['🌱','First Session!'], 5: ['⭐','5 Sessions!'], 10: ['🏆','10 Sessions!'], 25: ['💎','25 Sessions!'], 50: ['🚀','50 Sessions!'], 100: ['👑','100 Sessions!'] };
  if (SESSION_MILESTONES[sessions]) {
    const [emoji, label] = SESSION_MILESTONES[sessions];
    m.push({ emoji, label });
  }
  if (streak === 7)  m.push({ emoji: '🔥', label: '7-Day Streak!' });
  if (streak === 14) m.push({ emoji: '⚡', label: '2-Week Streak!' });
  if (streak === 30) m.push({ emoji: '🎯', label: '30-Day Streak!' });
  return m;
};

/* ─── Loading steps ──────────────────────────────────────────────────────── */
const LOADING_STEPS = [
  'Reading session notes…',
  'Analysing growth patterns…',
  'Crafting your child\'s story…',
  'Polishing the insight card…',
];

/* ─── Direction spark badge (3-pip system) ───────────────────────────────── */
const DIRECTION_CONFIG = {
  growing:      { label: 'Blooming',   pips: 3, color: '#16a34a', bg: 'rgba(220,252,231,0.85)', border: '#bbf7d0' },
  steady:       { label: 'Consistent', pips: 2, color: '#d97706', bg: 'rgba(254,243,199,0.85)', border: '#fde68a' },
  'needs care': { label: 'Budding',    pips: 1, color: '#e11d48', bg: 'rgba(255,228,230,0.85)', border: '#fecdd3' },
};

const DirectionChip = ({ direction }) => {
  const cfg = DIRECTION_CONFIG[direction] || DIRECTION_CONFIG.steady;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-bold text-[11px] whitespace-nowrap"
      style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}
    >
      <span className="flex items-center gap-0.5">
        {[1, 2, 3].map((n) => (
          <svg key={n} width="7" height="7" viewBox="0 0 7 7">
            <circle cx="3.5" cy="3.5" r="3.5" fill={n <= cfg.pips ? cfg.color : 'transparent'} stroke={cfg.color} strokeWidth="1.2" opacity={n <= cfg.pips ? 1 : 0.3} />
          </svg>
        ))}
      </span>
      {cfg.label}
    </span>
  );
};

/* ─── Trend badge ────────────────────────────────────────────────────────── */
const TrendBadge = ({ trend }) => {
  if (trend === 'up')   return <span className="inline-flex items-center gap-1 text-emerald-600 font-bold text-sm"><ArrowUp className="w-4 h-4" /> Up</span>;
  if (trend === 'down') return <span className="inline-flex items-center gap-1 text-rose-500 font-bold text-sm"><ArrowDown className="w-4 h-4" /> Down</span>;
  return <span className="inline-flex items-center gap-1 text-slate-400 font-bold text-sm"><Minus className="w-4 h-4" /> Stable</span>;
};

/* ─── 5. Per-dimension delta badge ──────────────────────────────────────── */
const DeltaBadge = ({ dimKey, internalScores, previousScores }) => {
  if (!previousScores || !internalScores) return null;
  const today = internalScores[dimKey];
  const prev  = previousScores[dimKey];
  if (today == null || prev == null) return null;
  const delta = parseFloat((today - prev).toFixed(1));
  if (Math.abs(delta) < 0.2) return null;
  const isUp = delta > 0;
  return (
    <span className={`text-[10px] font-black ml-1 ${isUp ? 'text-emerald-600' : 'text-rose-400'}`}>
      {isUp ? '▲' : '▼'}{Math.abs(delta)}
    </span>
  );
};

/* ─── Circular progress ring (uses animated display score) ───────────────── */
const CircleScore = ({ score, displayScore, max = 10, size = 100 }) => {
  const [ring, setRing] = useState(false);
  useEffect(() => { const t = setTimeout(() => setRing(true), 120); return () => clearTimeout(t); }, []);

  const cx    = size / 2;
  const cy    = size / 2;
  const r     = (size - 12) / 2;
  const circ  = 2 * Math.PI * r;
  const pct   = max > 0 ? score / max : 0;
  const dash  = ring ? circ * (1 - pct) : circ;
  const color = pct >= 0.7 ? '#16a34a' : pct >= 0.4 ? '#d97706' : '#dc2626';
  const label = pct >= 0.7 ? 'Great' : pct >= 0.4 ? 'Good' : 'Growing';

  return (
    <div className="flex flex-col items-center gap-1 shrink-0">
      {/*
        Use SVG-native transforms (not CSS) so html2canvas renders the
        ring and centred text correctly without needing CSS transform support.
      */}
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* track */}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e2e8f0" strokeWidth={8} />
        {/* progress arc — rotated via SVG attribute, not CSS */}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={8}
          strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={dash}
          transform={`rotate(-90 ${cx} ${cy})`}
          style={{ transition: 'stroke-dashoffset 1s cubic-bezier(.4,0,.2,1)' }} />
        {/* centred score — no rotation needed, dominantBaseline="central" is reliable */}
        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central"
          fill="#0f172a" fontSize={Math.round(size * 0.27)} fontWeight="800"
          fontFamily="system-ui,-apple-system,sans-serif">
          {displayScore ?? score ?? '—'}
        </text>
      </svg>
      <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color }}>{label}</span>
      <span className="text-[10px] text-slate-400">out of {max}</span>
    </div>
  );
};

/* ─── Animated score bar ─────────────────────────────────────────────────── */
const ScoreBar = ({ score, max, color, mounted }) => {
  const pct = max > 0 ? Math.round((score / max) * 100) : 0;
  return (
    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
      <div className="h-full rounded-full" style={{ width: mounted ? `${pct}%` : '0%', background: color, transition: 'width 0.9s cubic-bezier(.4,0,.2,1)' }} />
    </div>
  );
};

/* ─── Report score panel — Curiosity Response Index + Overall Growth Score ──
   Mirrors the same two metrics shown in the child's full Day's Insights
   report, so the card never shows an empty slot where the report's numbers
   should be. ──────────────────────────────────────────────────────────── */
const GROWTH_BAND_META = {
  balanced: { label: 'Balanced Growth', color: '#2563eb', emoji: '🔵' },
  moderate: { label: 'Moderate Growth', color: '#ca8a04', emoji: '🟡' },
  limited:  { label: 'Limited Growth',  color: '#dc2626', emoji: '🔴' },
};

const SessionScoreMeters = ({ report, mounted }) => {
  const cri  = report?.curiosity_response_index || {};
  const ogs  = report?.overall_growth_score || {};

  const criScore    = typeof cri.score === 'number' ? cri.score : (cri.score ? Number(cri.score) : null);
  const criMax      = cri.max_score || 10;
  const areasActive = ogs.areas_active ?? null;
  const areasTotal  = ogs.total_areas || 7;
  const bandMeta    = GROWTH_BAND_META[(ogs.rating || '').toLowerCase()];
  const bandColor   = bandMeta?.color || '#6366f1';

  if (criScore === null && areasActive === null) return null;

  return (
    <div className="bg-white/70 border border-amber-100 rounded-xl p-4 space-y-4">
      <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold flex items-center gap-1.5">
        <Zap className="w-3 h-3 text-amber-500" /> From Today's Report
      </p>

      {criScore !== null && (
        <div>
          <div className="flex items-end justify-between mb-1 gap-1">
            <span className="text-[10px] uppercase tracking-widest font-bold text-violet-600">🌈 Curiosity Response Index</span>
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">{criScore}/{criMax}</span>
          </div>
          <div className="flex items-end gap-1.5">
            <span className="text-2xl font-extrabold text-slate-900 leading-none">{criScore}</span>
            <span className="text-xs text-slate-400 mb-0.5">/ {criMax}</span>
          </div>
          <div className="mt-1.5">
            <ScoreBar score={criScore} max={criMax} color="#7c3aed" mounted={mounted} />
          </div>
          {cri.analysis && <p className="text-[11px] text-slate-600 mt-1.5 leading-snug">{cri.analysis}</p>}
        </div>
      )}

      {areasActive !== null && (
        <div className={criScore !== null ? 'pt-3 border-t border-amber-100/70' : ''}>
          <div className="flex items-end justify-between mb-1 gap-1">
            <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: bandColor }}>🧠 Overall Growth Score</span>
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: `${bandColor}18`, color: bandColor }}>
              {bandMeta ? `${bandMeta.emoji} ${bandMeta.label}` : `${areasActive}/${areasTotal}`}
            </span>
          </div>
          <div className="flex items-end gap-1.5">
            <span className="text-2xl font-extrabold text-slate-900 leading-none">{areasActive}</span>
            <span className="text-xs text-slate-400 mb-0.5">/ {areasTotal} areas active</span>
          </div>
          <div className="mt-1.5">
            <ScoreBar score={areasActive} max={areasTotal} color={bandColor} mounted={mounted} />
          </div>
          {ogs.summary && <p className="text-[11px] text-slate-600 mt-1.5 leading-snug">{ogs.summary}</p>}
        </div>
      )}
    </div>
  );
};

/* ─── 6. Sparkline ───────────────────────────────────────────────────────── */
const Sparkline = ({ history }) => {
  if (!history || history.length < 2) return null;
  const scores = history.map((h) => h.score);
  const minS = Math.min(...scores);
  const maxS = Math.max(...scores);
  const range = maxS - minS || 1;
  const W = 90, H = 34, PAD = 5;
  const xs = scores.map((_, i) => PAD + (i / (scores.length - 1)) * (W - PAD * 2));
  const ys = scores.map((s) => H - PAD - ((s - minS) / range) * (H - PAD * 2));
  const linePath = xs.map((x, i) => `${i === 0 ? 'M' : 'L'}${x},${ys[i]}`).join(' ');
  const fillPath = `${linePath} L${xs[xs.length - 1]},${H} L${xs[0]},${H} Z`;
  const lastIdx  = scores.length - 1;

  return (
    <div className="flex flex-col items-center gap-1">
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="overflow-visible">
        <defs>
          <linearGradient id="spark-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#f59e0b" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={fillPath} fill="url(#spark-fill)" />
        <path d={linePath} fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {xs.map((x, i) => (
          <circle key={i} cx={x} cy={ys[i]} r={i === lastIdx ? 3.5 : 2}
            fill={i === lastIdx ? '#f59e0b' : '#fde68a'} stroke="#d97706" strokeWidth="1" />
        ))}
      </svg>
      <span className="text-[9px] text-slate-400 uppercase tracking-wider">last {scores.length} sessions</span>
    </div>
  );
};

/* ─── Raw session transcript — reference only, kept outside cardRef so it
   never shows up in the downloaded/shared PNG. Lets whoever is reviewing the
   card check its accuracy against what the child actually said. ──────────── */
const TranscriptReference = ({ transcript }) => {
  const [expanded, setExpanded] = useState(false);
  if (!transcript) return null;

  return (
    <div className="w-full mx-auto mt-4" style={{ maxWidth: 980 }} data-testid="insight-card-transcript-reference">
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="w-full flex items-center justify-between gap-2 px-4 py-3 text-sm font-semibold text-slate-600 hover:bg-slate-50"
          data-testid="insight-card-transcript-toggle"
        >
          <span className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-slate-400" />
            Session Transcript
            <span className="text-xs font-normal text-slate-400">— for accuracy reference, not shared with parents</span>
          </span>
          {expanded ? <ChevronUp className="w-4 h-4 shrink-0" /> : <ChevronDown className="w-4 h-4 shrink-0" />}
        </button>
        {expanded && (
          <div className="px-4 pb-4">
            <pre
              className="whitespace-pre-wrap font-sans text-xs leading-relaxed text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-3 max-h-80 overflow-y-auto"
              data-testid="insight-card-transcript-body"
            >
              {transcript}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════════════════
   Main component
════════════════════════════════════════════════════════════════════════════ */
const InsightCard = ({ report, onClose, endpoint, tokenKey = 'parent_token' }) => {
  const cardRef = useRef(null);

  const [loading, setLoading]         = useState(true);
  const [loadStep, setLoadStep]       = useState(0);
  const [cardData, setCardData]       = useState(null);
  const [error, setError]             = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [mounted, setMounted]         = useState(false);

  /* 3. animated score counter */
  const [displayScore, setDisplayScore] = useState(0);
  /* 2. typewriter quote */
  const [displayedQuote, setDisplayedQuote] = useState('');

  /* ── derived card data ── */
  const pc            = cardData?.parent_card || {};
  const internal      = cardData?.internal    || {};
  const flag          = internal.flag         || {};
  const previousScores = cardData?.previous_scores || null;
  const scoreHistory  = cardData?.score_history   || [];
  const childName     = pc.child_name || report?.child_name || 'Your Child';
  const spotlightKey  = pc.spotlight_dimension || '';
  const spotlightLabel = SPOTLIGHT_LABELS[spotlightKey] || spotlightKey.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const SpotlightMeta = DIM_META[spotlightKey];

  /* 1. tone accent */
  const toneLabel = pc.emotional_tone?.label || '';
  const accent    = TONE_ACCENTS[toneLabel] || DEFAULT_ACCENT;

  /* 8. milestone badges */
  const milestones = useMemo(
    () => getMilestones(pc.sessions_completed || 0, pc.current_streak || 0),
    [pc.sessions_completed, pc.current_streak],
  );

  /* ── loading text cycle ── */
  useEffect(() => {
    if (!loading) return;
    const id = setInterval(() => setLoadStep((s) => (s + 1) % LOADING_STEPS.length), 1800);
    return () => clearInterval(id);
  }, [loading]);

  /* 3. Score counter — runs once on mount */
  useEffect(() => {
    if (!mounted || !pc.overall_today) return;
    const target   = pc.overall_today;
    const duration = 900;
    let startTime  = null;
    const step = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      const eased    = 1 - Math.pow(1 - progress, 3);
      setDisplayScore(parseFloat((eased * target).toFixed(1)));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [mounted, pc.overall_today]);

  /* 2. Typewriter — runs once on mount */
  useEffect(() => {
    if (!mounted || !pc.child_quote) return;
    const quote    = pc.child_quote;
    const totalMs  = 1400;
    const interval = Math.max(14, Math.floor(totalMs / quote.length));
    let i = 0;
    setDisplayedQuote('');
    const id = setInterval(() => {
      i++;
      setDisplayedQuote(quote.slice(0, i));
      if (i >= quote.length) clearInterval(id);
    }, interval);
    return () => clearInterval(id);
  }, [mounted, pc.child_quote]);

  /* ── fetch ── */
  const fetchCard = useCallback(() => {
    if (!report?.id) return;
    const token = localStorage.getItem(tokenKey);
    if (!token) return;
    setLoading(true);
    setError(null);
    setMounted(false);
    setDisplayScore(0);
    setDisplayedQuote('');

    const url       = endpoint || `${BACKEND_URL}/api/parent/insight-card/generate`;
    const isObs     = tokenKey === 'observer_token';
    const reqUrl    = isObs ? `${url}?token=${encodeURIComponent(token)}` : url;
    const config    = isObs ? {} : { headers: { Authorization: `Bearer ${token}` } };

    axios.post(reqUrl, { report_id: report.id }, config)
      .then(({ data }) => { setCardData(data); setTimeout(() => setMounted(true), 80); })
      .catch((e) => setError(e.response?.data?.detail || e.message || 'Could not generate insight card.'))
      .finally(() => setLoading(false));
  }, [report?.id, endpoint, tokenKey]);

  useEffect(() => { fetchCard(); }, [fetchCard]);

  /* ── download PNG ── */
  const downloadPNG = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        backgroundColor: '#FBF6EC',
        useCORS: true,
        allowTaint: true,
        logging: false,
        /*
         * windowWidth forces the html2canvas iframe to behave as a 820 px
         * desktop viewport regardless of the physical device screen width.
         * This makes every sm: (≥640 px) and md: (≥768 px) Tailwind class
         * fire during capture, so the downloaded PNG always matches the
         * desktop layout — even when the user downloads from a phone.
         */
        windowWidth: 820,
        onclone: (_clonedDoc, clonedEl) => {
          const freeze = _clonedDoc.createElement('style');
          freeze.textContent = `
            * {
              animation: none !important;
              transition: none !important;
              backdrop-filter: none !important;
              -webkit-backdrop-filter: none !important;
            }
            .ic-section,
            .ic-dim-row,
            .ic-milestone {
              opacity: 1 !important;
              transform: none !important;
            }
          `;
          _clonedDoc.head.appendChild(freeze);
        },
      });
      const link = document.createElement('a');
      const name = (pc.child_name || report?.child_name || 'Child').replace(/\s+/g, '_');
      const date = (pc.date || '').replace(/-/g, '');
      link.download = `${name}_Insight_${date}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch { alert('Could not generate image. Try a desktop browser.'); }
    finally { setDownloading(false); }
  };

  /* 7. WhatsApp share */
  const shareWhatsApp = () => {
    const lines = [
      `✨ *${childName}'s Daily Insight from Sanjaya*`,
      ``,
      `📊 Today's Score: *${pc.overall_today}/10*${toneLabel ? ` — ${toneLabel}` : ''}`,
      `🌟 Spotlight: *${spotlightLabel}*`,
      pc.celebrate_today ? `🎉 ${pc.celebrate_today}` : '',
      pc.parent_nudge    ? `💡 ${pc.parent_nudge}`    : '',
      ``,
      `_With care, always — Sanjaya_`,
    ].filter((l) => l !== null && l !== undefined && !(l === '' && lines?.length));
    window.open(`https://wa.me/?text=${encodeURIComponent(lines.join('\n'))}`, '_blank');
  };

  /* ════════════════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════════════════ */
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-2 sm:p-6"
      style={{ background: 'rgba(15,23,42,0.60)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
      data-testid="insight-card-modal"
    >
      <div
        className="bg-white rounded-2xl max-w-4xl w-full my-4 shadow-2xl overflow-hidden"
        style={{ animation: 'ic-slide-up 0.3s cubic-bezier(.4,0,.2,1)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <style>{`
          @keyframes ic-slide-up  { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
          @keyframes ic-fade-in   { from{opacity:0;transform:translateY(8px)}  to{opacity:1;transform:translateY(0)} }
          @keyframes ic-pop       { 0%{opacity:0;transform:scale(0) rotate(-10deg)} 70%{transform:scale(1.15) rotate(3deg)} 100%{opacity:1;transform:scale(1) rotate(0)} }
          @keyframes ic-row-in    { from{opacity:0;transform:translateX(-10px)} to{opacity:1;transform:translateX(0)} }
          .ic-section  { animation: ic-fade-in 0.4s ease both; }
          .ic-milestone{ animation: ic-pop 0.5s cubic-bezier(.34,1.56,.64,1) both; }
          .ic-dim-row  { animation: ic-row-in 0.35s ease both; }
        `}</style>

        {/* ── Toolbar ─────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100 bg-white sticky top-0 z-10">
          <h2 className="font-bold text-slate-800 text-sm flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5 text-white" />
            </div>
            Daily Insight Card
          </h2>
          <div className="flex items-center gap-2">
            {!loading && error && (
              <Button size="sm" variant="outline" onClick={fetchCard} className="gap-1.5 text-slate-600 h-8">
                <RefreshCw className="w-3.5 h-3.5" /> Retry
              </Button>
            )}
            {!loading && !error && cardData && (
              <>
                {/* 7. WhatsApp */}
                <Button size="sm" variant="outline" onClick={shareWhatsApp}
                  className="gap-1.5 h-8 border-green-200 text-green-700 hover:bg-green-50">
                  <Share2 className="w-3.5 h-3.5" /> WhatsApp
                </Button>
                <Button size="sm" onClick={downloadPNG} disabled={downloading}
                  className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white gap-1.5 h-8 shadow-sm"
                  data-testid="insight-card-download-btn">
                  {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                  {downloading ? 'Generating…' : 'Download PNG'}
                </Button>
              </>
            )}
            <Button size="sm" variant="ghost" onClick={onClose} className="h-8 w-8 p-0 text-slate-400 hover:text-slate-700" data-testid="insight-card-close-btn">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* ── Loading ──────────────────────────────────────────────────── */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-24 gap-5 bg-gradient-to-b from-amber-50/40 to-white">
            <div className="relative">
              <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-amber-500" />
              </div>
              <div className="absolute inset-0 rounded-full border-4 border-amber-300 border-t-transparent animate-spin" />
            </div>
            <div className="text-center px-6">
              <p className="font-bold text-slate-800 text-lg">Sanjaya is crafting today's card…</p>
              <p className="text-slate-500 text-sm mt-2 min-h-[20px]">{LOADING_STEPS[loadStep]}</p>
            </div>
            <div className="flex gap-2 mt-1">
              {LOADING_STEPS.map((_, i) => (
                <div key={i} className="rounded-full transition-all duration-300"
                  style={{ width: i === loadStep ? 20 : 8, height: 8, background: i === loadStep ? '#f59e0b' : '#fde68a' }} />
              ))}
            </div>
          </div>
        )}

        {/* ── Error ────────────────────────────────────────────────────── */}
        {!loading && error && (
          <div className="p-12 flex flex-col items-center gap-4 bg-rose-50/30">
            <div className="w-14 h-14 rounded-full bg-rose-100 flex items-center justify-center">
              <AlertCircle className="w-7 h-7 text-rose-500" />
            </div>
            <div className="text-center">
              <p className="font-bold text-slate-800 text-base">Could not generate insight card</p>
              <p className="text-slate-500 text-sm mt-1 max-w-sm">{error}</p>
            </div>
            <Button onClick={fetchCard} className="gap-2 bg-rose-500 hover:bg-rose-600 text-white">
              <RefreshCw className="w-4 h-4" /> Try Again
            </Button>
          </div>
        )}

        {/* ── Card content ─────────────────────────────────────────────── */}
        {!loading && !error && cardData && (
          <div className="p-4 sm:p-6" style={{ background: 'linear-gradient(180deg,#fefce8 0%,#f8fafc 100%)' }}>
            <div
              ref={cardRef}
              className="w-full mx-auto rounded-2xl overflow-hidden shadow-lg"
              style={{ maxWidth: 980, fontFamily: 'Montserrat,system-ui,-apple-system,"Segoe UI",sans-serif', background: 'linear-gradient(160deg,#FFFBF0 0%,#FDF8EC 50%,#F9F4E8 100%)', border: '1.5px solid #E8DBC1' }}
            >
              {/* 1. Tone-aware accent bar */}
              <div className="h-1.5 w-full" style={{ background: accent.bar }} />

              <div className="p-5 sm:p-7">

                {/* ── HEADER ─────────────────────────────────────────── */}
                <div className="ic-section flex flex-col sm:flex-row items-start justify-between gap-4 pb-5 border-b border-amber-200/60" style={{ animationDelay: '0ms' }}>
                  <div className="flex items-start gap-4">
                    {/* Avatar wrapper sized to include the badge — no negative overflow so html2canvas renders cleanly */}
                    <div className="relative shrink-0" style={{ width: 76, height: 76 }}>
                      <div className="absolute top-0 left-0 w-16 h-16 rounded-2xl flex items-center justify-center text-2xl font-black text-white shadow-md"
                        style={{ background: 'linear-gradient(135deg,#F0A820 0%,#e8820a 100%)' }}>
                        {childName.charAt(0).toUpperCase()}
                      </div>
                      <div className="absolute bottom-0 right-0 w-6 h-6 rounded-full bg-emerald-500 border-2 border-white flex items-center justify-center">
                        <CheckCircle2 className="w-3 h-3 text-white fill-white" />
                      </div>
                    </div>
                    <div>
                      <h1 className="text-3xl sm:text-4xl font-black text-slate-900 leading-none" style={{ fontFamily: 'Georgia,"Playfair Display",serif' }}>
                        {childName}
                      </h1>
                      <div className="flex items-center flex-wrap gap-2 mt-1.5">
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider text-amber-800 bg-amber-100 border border-amber-200">
                          <Leaf className="w-3 h-3 text-green-600" /> Daily Insight
                        </span>
                        {/* 8. Milestone badges */}
                        {milestones.map((ms, i) => (
                          <span key={i} className="ic-milestone inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-gradient-to-r from-amber-400 to-orange-400 text-white shadow-sm"
                            style={{ animationDelay: `${300 + i * 100}ms` }}>
                            {ms.emoji} {ms.label}
                          </span>
                        ))}
                      </div>
                      <p className="text-xs text-slate-500 mt-1.5 italic">— {pc.observer_name} (Observer)</p>
                    </div>
                  </div>

                  <div className="flex flex-row sm:flex-col gap-3 sm:gap-2 flex-wrap sm:text-right">
                    {pc.date && (
                      <div className="flex items-center gap-2 sm:justify-end">
                        <div className="w-8 h-8 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center">
                          <Calendar className="w-4 h-4 text-amber-600" />
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Date</div>
                          <div className="font-semibold text-slate-800 text-xs leading-tight">
                            {(() => {
                              const raw = (pc.date || '').slice(0, 10);
                              const [y, m, d] = raw.split('-').map(Number);
                              if (!y || !m || !d) return raw;
                              return new Date(y, m - 1, d).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' });
                            })()}
                          </div>
                          {pc.days_enrolled > 0 && <div className="text-[10px] text-slate-400">Day {pc.days_enrolled} with Sanjaya</div>}
                        </div>
                      </div>
                    )}
                    {pc.emotional_tone?.emoji && (
                      <div className="flex items-center gap-2 sm:justify-end">
                        <div className="w-8 h-8 rounded-lg bg-green-50 border border-green-200 flex items-center justify-center text-base">
                          {pc.emotional_tone.emoji}
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Today's Tone</div>
                          <div className="font-bold text-slate-800 text-sm leading-tight">{pc.emotional_tone.label}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* ── 2. QUOTE HERO (typewriter + tone bg) ───────────── */}
                {/* ── Main Topic Banner ──────────────────────────── */}
                {pc.main_topic && (
                  <div className="ic-section mt-5 rounded-xl px-4 py-3 flex items-start gap-3"
                    style={{ animationDelay: '50ms', background: 'rgba(254,243,199,0.7)', border: '1px solid #fde68a' }}>
                    <div className="shrink-0 w-7 h-7 rounded-lg bg-amber-400 flex items-center justify-center mt-0.5">
                      <MessageCircle className="w-3.5 h-3.5 text-white" />
                    </div>
                    <div>
                      <span className="text-[10px] uppercase tracking-widest text-amber-700 font-black block mb-0.5">Today's Conversation</span>
                      <p className="text-sm text-slate-800 leading-relaxed">{pc.main_topic}</p>
                    </div>
                  </div>
                )}

                <div className="ic-section mt-5 rounded-2xl p-5 sm:p-6 relative overflow-hidden"
                  style={{ animationDelay: '60ms', background: `linear-gradient(135deg,${accent.quoteFrom} 0%,${accent.quoteTo} 100%)`, boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6)' }}>
                  <div className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-10" style={{ background: '#F0A820', transform: 'translate(25%,-25%)' }} />
                  <div className="absolute bottom-0 left-0 w-24 h-24 rounded-full opacity-10" style={{ background: '#254B70', transform: 'translate(-25%,25%)' }} />
                  <Quote className="w-10 h-10 text-amber-700/20 absolute top-4 left-4" />
                  <div className="relative z-10 pl-12">
                    <p className="text-xl sm:text-2xl font-bold text-slate-900 italic leading-snug min-h-[2em]" style={{ fontFamily: 'Georgia,serif' }}>
                      &ldquo;{displayedQuote || pc.child_quote || 'Today was a meaningful session.'}&rdquo;
                      {displayedQuote && displayedQuote.length < (pc.child_quote || '').length && (
                        <span className="inline-block w-0.5 h-5 bg-slate-700 ml-0.5 animate-pulse align-middle" />
                      )}
                    </p>
                    <div className="flex items-center gap-2 mt-3">
                      <div className="h-px flex-1 bg-amber-400/30" />
                      <p className="text-sm text-slate-700 font-semibold italic">— {childName}</p>
                      <span className="text-base">✨</span>
                    </div>
                  </div>
                </div>

                {/* ── TWO-COLUMN ─────────────────────────────────────── */}
                <div className="ic-section mt-5 grid grid-cols-1 md:grid-cols-5 gap-4" style={{ animationDelay: '120ms' }}>

                  {/* Spotlight */}
                  <div className="md:col-span-2 rounded-xl p-4 border" style={{ background: 'rgba(255,255,255,0.75)', borderColor: '#E8DBC1', backdropFilter: 'blur(4px)' }}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm">
                        <Star className="w-4 h-4 text-white fill-white" />
                      </div>
                      <span className="text-[10px] uppercase tracking-widest text-amber-700 font-black">Today's Spotlight</span>
                    </div>
                    <h3 className="text-xl font-black text-slate-900 leading-tight mb-1" style={{ fontFamily: 'Georgia,serif' }}>
                      {spotlightLabel}
                    </h3>
                    {SpotlightMeta && <span className="text-2xl">{SpotlightMeta.emoji}</span>}
                    <p className="text-sm text-slate-700 leading-relaxed mt-2">{pc.spotlight_story}</p>

                    {pc.why_it_matters && (
                      <div className="mt-4 pt-3 border-t border-amber-200/40">
                        <div className="flex items-center gap-1.5 mb-1"><Target className="w-3.5 h-3.5 text-emerald-600" /><span className="text-[10px] uppercase tracking-widest text-emerald-700 font-black">Why It Matters</span></div>
                        <p className="text-sm text-slate-700 leading-relaxed">{pc.why_it_matters}</p>
                      </div>
                    )}
                    {pc.observer_note && (
                      <div className="mt-3 pt-3 border-t border-amber-200/40">
                        <div className="flex items-center gap-1.5 mb-1"><MessageCircle className="w-3.5 h-3.5 text-violet-600" /><span className="text-[10px] uppercase tracking-widest text-violet-700 font-black">Observer's Note</span></div>
                        <p className="text-sm text-slate-700 leading-relaxed">{pc.observer_note}</p>
                      </div>
                    )}
                    {pc.parent_nudge && (
                      <div className="mt-3 pt-3 border-t border-amber-200/40">
                        <div className="flex items-center gap-1.5 mb-1"><Heart className="w-3.5 h-3.5 text-rose-500" /><span className="text-[10px] uppercase tracking-widest text-rose-600 font-black">Parent Nudge</span></div>
                        <p className="text-sm text-slate-700 leading-relaxed">{pc.parent_nudge}</p>
                      </div>
                    )}
                  </div>

                  {/* Right column */}
                  <div className="md:col-span-3 flex flex-col gap-3">

                    {/* 4. Dimensions table — staggered row entry + 5. delta badges */}
                    <div className="rounded-xl p-4 border" style={{ background: 'rgba(255,255,255,0.75)', borderColor: '#E8DBC1', backdropFilter: 'blur(4px)' }}>
                      <p className="text-[10px] uppercase tracking-widest text-slate-400 font-black mb-3 flex items-center gap-1.5">
                        <Award className="w-3 h-3 text-amber-500" /> How {childName} showed up today
                      </p>
                      <div className="space-y-1">
                        {Object.entries(pc.dimensions_direction || {}).map(([key, direction], idx) => {
                          const meta = DIM_META[key];
                          if (!meta) return null;
                          const Icon = meta.icon;
                          return (
                            <div
                              key={key}
                              className="ic-dim-row flex items-center gap-3 py-2 px-2 rounded-lg border border-transparent hover:bg-amber-50/60 hover:border-amber-100 transition-colors duration-150"
                              style={{ animationDelay: mounted ? `${idx * 55}ms` : '0ms' }}
                            >
                              <div className={`w-7 h-7 rounded-lg ${meta.ring} flex items-center justify-center shrink-0`}>
                                <Icon className={`w-3.5 h-3.5 ${meta.accent}`} />
                              </div>
                              <span className="flex-1 text-sm text-slate-800 font-semibold flex items-center gap-1">
                                {meta.label}
                                {/* 5. per-dimension delta */}
                                <DeltaBadge dimKey={key} internalScores={internal.scores} previousScores={previousScores} />
                              </span>
                              <DirectionChip direction={direction} />
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Curiosity Response Index + Overall Growth Score, pulled from the report */}
                    <SessionScoreMeters report={report} mounted={mounted} />
                  </div>
                </div>

                {/* ── BOTTOM TWO CARDS ─────────────────────────────── */}
                <div className="ic-section mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3" style={{ animationDelay: '180ms' }}>
                  {pc.celebrate_today && (
                    <div className="rounded-xl p-5 border border-emerald-100 flex flex-col gap-3 relative overflow-hidden"
                      style={{ background: 'linear-gradient(135deg,rgba(240,253,244,0.95),rgba(220,252,231,0.80))' }}>
                      <div className="absolute top-0 right-0 w-28 h-28 rounded-full bg-emerald-200/30" style={{ transform: 'translate(35%,-35%)' }} />
                      <div className="absolute bottom-0 left-0 w-16 h-16 rounded-full bg-emerald-100/40" style={{ transform: 'translate(-30%,30%)' }} />
                      <div className="flex items-center gap-2 relative z-10">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-400 to-green-600 flex items-center justify-center shrink-0 shadow-sm">
                          <Sparkles className="w-4 h-4 text-white" />
                        </div>
                        <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-black">Celebrate Today</div>
                      </div>
                      <div className="relative z-10 pl-1">
                        <Quote className="w-4 h-4 text-emerald-300 mb-1" />
                        <p className="text-sm text-slate-700 leading-relaxed font-medium">{pc.celebrate_today}</p>
                        <div className="mt-3 pt-3 border-t border-emerald-100 flex items-center gap-1.5">
                          <Heart className="w-3.5 h-3.5 text-emerald-500" />
                          <span className="text-[11px] text-emerald-600 font-semibold">A moment worth remembering</span>
                        </div>
                      </div>
                    </div>
                  )}
                  {pc.growth_spark && (
                    <div className="rounded-xl p-5 border border-amber-100 flex flex-col gap-3 relative overflow-hidden"
                      style={{ background: 'linear-gradient(135deg,rgba(255,251,235,0.95),rgba(254,243,199,0.80))' }}>
                      <div className="absolute top-0 right-0 w-28 h-28 rounded-full bg-amber-200/30" style={{ transform: 'translate(35%,-35%)' }} />
                      <div className="absolute bottom-0 left-0 w-16 h-16 rounded-full bg-amber-100/40" style={{ transform: 'translate(-30%,30%)' }} />
                      <div className="flex items-center gap-2 relative z-10">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shrink-0 shadow-sm">
                          <TrendingUp className="w-4 h-4 text-white" />
                        </div>
                        <div className="text-[10px] uppercase tracking-widest text-amber-700 font-black">Growth Spark</div>
                      </div>
                      <div className="relative z-10 pl-1">
                        <Leaf className="w-4 h-4 text-amber-300 mb-1" />
                        <p className="text-sm text-slate-700 leading-relaxed font-medium">{pc.growth_spark}</p>
                        <div className="mt-3 pt-3 border-t border-amber-100 flex items-center gap-1.5">
                          <Award className="w-3.5 h-3.5 text-amber-500" />
                          <span className="text-[11px] text-amber-600 font-semibold">Progress in action</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* ── FLAG STATUS ─────────────────────────────────────── */}
                <div className="ic-section mt-4 rounded-xl p-3 border flex items-center gap-3"
                  style={{ animationDelay: '220ms', background: flag.raised ? 'rgba(255,241,242,0.9)' : 'rgba(240,253,244,0.9)', borderColor: flag.raised ? '#fecdd3' : '#bbf7d0' }}>
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center shadow-sm ${flag.raised ? 'bg-rose-500' : 'bg-emerald-500'}`}>
                    <Shield className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className={`text-[10px] uppercase tracking-widest font-black ${flag.raised ? 'text-rose-700' : 'text-emerald-700'}`}>
                      {flag.raised ? `Flag — ${flag.level || 'raised'}` : 'All Clear Today'}
                    </div>
                    <div className="text-sm text-slate-700">
                      {flag.raised ? (flag.note || 'Principal has been notified.') : `No concerns noted. ${childName}'s space is safe and supported.`}
                    </div>
                  </div>
                </div>

                {/* ── FOOTER ─────────────────────────────────────────── */}
                <div className="mt-5 pt-4 border-t border-amber-200/50 text-center">
                  <p className="text-xs text-slate-400 italic flex items-center justify-center gap-2">
                    <Leaf className="w-3.5 h-3.5 text-green-500" />
                    Thank you for giving {childName} the gift of being heard, every day.
                    <Heart className="w-3.5 h-3.5 text-rose-400 fill-rose-400" />
                  </p>
                  <div className="flex items-center justify-center gap-2 mt-2">
                    <div className="h-px w-16 bg-gradient-to-r from-transparent to-amber-300" />
                    <span className="text-[10px] text-amber-600 font-bold tracking-widest uppercase">Sanjaya</span>
                    <div className="h-px w-16 bg-gradient-to-l from-transparent to-amber-300" />
                  </div>
                </div>

              </div>
            </div>

            <TranscriptReference transcript={cardData.transcript || report?.transcript} />
          </div>
        )}
      </div>
    </div>
  );
};

export default InsightCard;
